/**
 * Selector building for the operator console log filter. Meteor-free and
 * pure: the terminal applies the result to its client-side collection, so one
 * selector covers both the lines already on screen and every line that
 * arrives while the filter is active -- the live stream itself is untouched.
 *
 * Extensible by design: each filter criterion contributes one independent
 * clause and the clauses are ANDed. A future criterion (fleet, mode, level,
 * event type) is a new optional field here plus its clause -- no change to
 * the terminal's plumbing.
 */

export interface TerminalFilters {
  /** Case-insensitive substring of the bot name. A shared prefix ('Shred')
   *  is how an operator scopes to a SET of bots, so substring, not exact. */
  bot?: string;
  /** True when the value was PICKED from the list rather than typed: an
   *  exact match. 'MogBots' typed also matches MogBots1..MogBots9; picked,
   *  it means that one bot. */
  botExact?: boolean;
}

/** Escape a user-typed string for literal use inside a regex. */
export const escapeRegex = (s: string): string => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/**
 * The minimongo selector for the given filters, ANDed with an optional fixed
 * scope (the bot-page terminal is hard-scoped to its own bot; the operator's
 * filter narrows within that, never widens past it).
 */
export const terminalSelector = (
  filters: TerminalFilters,
  scope: { bot?: string } = {},
): Record<string, unknown> => {
  const clauses: Record<string, unknown>[] = [];
  if (scope.bot) clauses.push({ bot: scope.bot });
  const botText = filters.bot?.trim();
  if (botText) {
    clauses.push(filters.botExact
      ? { bot: botText }
      : { bot: { $regex: escapeRegex(botText), $options: 'i' } });
  }
  if (!clauses.length) return {};
  if (clauses.length === 1) return clauses[0]!;
  return { $and: clauses };
};

/** Picklist options for the typed text: the distinct bot names matching it
 *  case-insensitively, sorted. Empty text offers every name. */
export const filterBotOptions = (names: string[], text: string): string[] => {
  const t = text.trim().toLowerCase();
  const uniq = [...new Set(names)].sort((a, b) => a.localeCompare(b));
  return t ? uniq.filter((n) => n.toLowerCase().includes(t)) : uniq;
};
